import React, { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { Button } from '@oforce/global-components';

import { useDownloadFile } from '../../hooks';
import { useNotificationStatusReportJobContext } from '../../context';
import { ToastContent } from '../../components/react-toastify';

export default function NotificationStatusReportJob() {
  const { jobId, message, finished, filename, url, error, jobErrors, clearJobId } =
    useNotificationStatusReportJobContext();
  const toastId = useRef(null);

  useEffect(() => {
    if (!toast.current && jobId) {
      toastId.current = toast(<NotificationStatusReport />, { autoClose: false, closeOnClick: false });
    }
  }, [toastId, jobId, clearJobId]);

  useEffect(() => {
    if (!toastId.current) {
      return;
    }

    if (error || jobErrors.length !== 0) {
      toast.update(toastId.current, {
        render: () => <ErrorNotificationStatusReport error={jobErrors || error} />,
        onClose: clearJobId
      });
    } else if (!finished && !url) {
      toast.update(toastId.current, {
        render: () => <NotificationStatusReport message={message} />
      });
    } else if (finished) {
      toast.update(toastId.current, {
        render: () => (
          <DownloadNotificationStatusReport
            message={message}
            url={url}
            filename={filename}
            errors={jobErrors}
            clearJobId={clearJobId}
          />
        ),
        onClose: clearJobId
      });
    }
    // eslint-disable-next-line
  }, [toastId, message, filename, url, finished, error, jobErrors]);

  return <></>;
}

const NotificationStatusReport = ({ message }) => {
  let title = message ?? 'Preparing Notification Status Report';

  return (
    <ToastContent
      title={title}
      icon={['far', 'spinner-third']}
      iconProps={{ spin: true }}
      message="Please allow us some time to generate the report."
    />
  );
};

const ErrorNotificationStatusReport = ({ error }) => (
  <ToastContent
    title="Error Generating Notification Status Report"
    titleProps={{ className: 'text-danger' }}
    icon={['fas', 'times-circle']}
    message={error.message || error}
  />
);

const DownloadNotificationStatusReport = ({ message, url, filename, errors, clearJobId }) => {
  const { download, loading } = useDownloadFile({ url, filename });

  const downloadAndClearJobId = () => {
    download();
    clearJobId();
  };

  return (
    <ToastContent
      title={message || 'Notification Status Report Ready'}
      titleProps={{ className: 'text-success' }}
      icon={url ? ['fas', 'check-circle'] : ['far', 'exclamation-triangle']}
      message={url && 'Your notification status report is ready for download.'}
    >
      {errors && errors.length > 0 && errors.map(e => <div key={e}>{e}</div>)}
      <div>
        {url && (
          <Button
            color="link"
            size="sm"
            icon={['far', 'arrow-to-bottom']}
            className="mr-2"
            loading={loading}
            loadingText="Downloading..."
            onClick={downloadAndClearJobId}
          >
            Download Now
          </Button>
        )}
      </div>
    </ToastContent>
  );
};
